<template>
    <div>
        <b-form-group>
            <b-form-group  v-if="NodeSearchTypes.length > 1" class="d-flex justify-content-end">
                <!-- Bouton bascule entre : recherche par noeud / recherche par propriété par rapport au périmètre -->
                <!-- Bouton bascule entre : recherche de noeud / recherche par type -->
                <b-button-group class="btn-gray-light rounded-pill border border-gray" size="sm">
                        <!-- Recherche de noeud -->
                        <b-button v-b-tooltip.hover 
                            v-if="NodeSearchTypes.includes(TypeSearchEnum.NameSearch)"
                            @click="changePinType(TypeSearchEnum.NameSearch)" 
                            :title="$t('NodeSearch')" 
                            variant="outline-white" 
                            :pressed="typeSearch === TypeSearchEnum.NameSearch"
                            :class="typeSearch === TypeSearchEnum.NameSearch ? 'border-gray-dark' : ''"
                            pill>
                            <b-icon icon="search" />
                        </b-button>
                        <!-- Recherche par propriété -->
                        <b-button v-b-tooltip.hover 
                            v-if="NodeSearchTypes.includes(TypeSearchEnum.TypeSearch)"
                            @click="changePinType(TypeSearchEnum.TypeSearch)" 
                            :title="$t('SearchByType')"
                            variant="outline-white"
                            :pressed="typeSearch === TypeSearchEnum.TypeSearch"
                            :class="typeSearch === TypeSearchEnum.TypeSearch ? 'border-gray-dark' : ''"
                            pill>
                            <b-icon icon="puzzle" />
                        </b-button>
                        <!-- Utilisation de la Pin racine -->
                        <b-button v-b-tooltip.hover 
                            v-if="NodeSearchTypes.includes(TypeSearchEnum.PerimeterSearch)"
                            @click="changePinType(TypeSearchEnum.PerimeterSearch)" 
                            :title="$t('UserPerimeter')"
                            variant="outline-white"
                            :pressed="typeSearch === TypeSearchEnum.PerimeterSearch"
                            :class="typeSearch === TypeSearchEnum.PerimeterSearch ? 'border-gray-dark' : ''"
                            pill>
                            <b-icon icon="diagram3" />
                        </b-button>
                    </b-button-group>
            </b-form-group>
            <!--INPUT de recherche d'objet-->
            <b-input-group v-if="typeSearch === TypeSearchEnum.NameSearch">
                <b-input-group-prepend v-if="pins[0] && pins[0]?.Type === pinTypeEnum.Root" is-text>
                    <div class="d-flex align-items-center px-1 border-1">
                        <b-img :src="AbsoluteLink('Icons/' + pins[0]?.ObjectType + '_Icon.svg')" height="18rem" width="18rem" />
                    </div>
                </b-input-group-prepend>
                <b-input
                    :placeholder="$t('SearchFilling')"
                    autofocus
                    :readonly="SearchReadOnly"
                    :value="pins && pins[0] ? pins[0].Text : ''"
                    :state="searchState"
                    v-b-tooltip.hover
                    @click="SearchVisible = true"
                    :title="$t('HeritageSearch')"/>
                <b-input-group-append>
                    <b-button :variant="Variant" @click="SearchVisible = true">
                        <b-icon icon="search"/>
                    </b-button>
                </b-input-group-append>
                <b-form-invalid-feedback :state="searchState">
                    {{ $t('PleaseSearchANode') }}
                </b-form-invalid-feedback>
            </b-input-group>
            <b-input-group v-if="typeSearch === TypeSearchEnum.TypeSearch">
                <b-input-group-prepend is-text>
                    <div class="d-flex align-items-center px-1 border-1">
                        <b>{{ $t('ObjectType') }}</b>
                    </div>
                </b-input-group-prepend>
                <b-form-select :options="listItems('ObjectType')" v-model="pinType" @input="pins=[filterTreeUtils.SetPinNone(pinType.toString(), Preview)]" plain value-field="Id" text-field="Label"/>
            </b-input-group>
            <b-alert v-if="typeSearch === TypeSearchEnum.PerimeterSearch" :variant="Variant + '-dark'" show class="m-1 p-2">{{ $t('UserPerimeter') }}</b-alert>
        </b-form-group>
        <!--Composant de recherche d'objet-->
        <Search v-if="SearchVisible" 
            :Visible.sync="SearchVisible" 
            :SearchNode.sync="searchNode" 
            :TypesList="objectTypes(WidgetName)" 
            :Variant="Variant" 
            @NodeSelectedEvent="NodeSelectedEvent"
            @ShortMeterSelectedEvent="ShortMeterSelectedEvent"
            @ShortLBOSelectedEvent="ShortLBOSelectedEvent"/>
    </div>
</template>
<script lang="ts">

/** Vue / Decorateurs */
import { Component, PropSync, InjectReactive, Prop, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { Getter } from 'vuex-class';

/** Utilitaires */
import { ThemeUtils, LinkUtils, FilterTreeUtils } from "@/components/commons/Utils";

/** Composants */
import Search from "@/components/commons/Search.vue";

/** Classes / Enum */
import PinTypeEnum from '@/enums/PinTypeEnum';
import BaseNode from '@/types/BaseNode';
import Pin from '@/types/FilterTree/Pin';
import TypeSearchEnum from '@/enums/Dashboard/TypeSearchEnum';
import ListItem from '@/types/fields/ListItem';
import BaseObjectTypeEnum from '@/enums/BaseObjectTypeEnum';

/**
 * Composant permettant de selectionner un noeud suivant le type de recherche
 * Rechercher par propriété 
 * Rechercher dans l'arborescence
 * Rechercher par périmètre utilisateur
 */
@Component({
    components: {
        Search
    }
})
export default class NodesSelector extends mixins(ThemeUtils, LinkUtils) {

    //#region Propriétés pour la recherche d'objet

    /** Enum pour désigner le type d'objet */
    public pinTypeEnum = PinTypeEnum;
    public pinType: PinTypeEnum = PinTypeEnum.Tree;

    /** Enum pour désigner le type de recherche d'objet */
    private TypeSearchEnum = TypeSearchEnum;
    private typeSearch: TypeSearchEnum = TypeSearchEnum.NameSearch;
    private filterTreeUtils = FilterTreeUtils;

    /** Retourne le noeud cherché */
    private searchNode: BaseNode = {} as BaseNode;
        
    /** Booleen affichage fenetre de recherche de noeud */
    public SearchVisible: boolean = false;
    
    public searchState: boolean = null;
    
    /** Getter pour obtenir une liste */
    @Getter('GetListItems')
    private listItems!: (listName: string) => ListItem[];
    
    //#end region

    //#region Propriétés pour l'initialisation du composant
    
	/** Variant de couleur à appliquer sur le composant */
	@InjectReactive()
	public Variant!: string;

    /** Pins renvoyé au composant parent */
    @PropSync("Pins", { default: () => []})
    public pins!: Pin[];

    /** Nom du widget parent */
    @Prop({ default: () => "" })
    private WidgetName!: string;

    /** Si widget est en mode preview */ 
    @Prop({ default: () => false })
    public Preview!: boolean;
    
    /** Types de recherche réalisable sur le widget */ 
    @Prop({ default: () => [] })
    public NodeSearchTypes!: TypeSearchEnum[];
    
    /** Indication si la recherche est en lecture seule */
    @Prop({default: () => false})
    public SearchReadOnly!: boolean;

    /** Méthode pour la récupération des types objets du widget */
    @Getter('WidgetLibraryStore/GetWidgetObjectTypes')
    private objectTypes: (widgetType: string)=> string[];
    
    private searchMap = {
        [PinTypeEnum.Tree]: TypeSearchEnum.NameSearch,
        [PinTypeEnum.Root]: TypeSearchEnum.PerimeterSearch,
        [PinTypeEnum.None]: TypeSearchEnum.TypeSearch
    }

    /**
     * Evenement déclaré pour notifier l'evenement de recherche d'un noeud
     */
    private NodeSelectedEvent = () => {
        this.$emit('NodeSelectedEvent');
    };

    /**
     * Evenement déclaré pour notifier l'evenement de recherche d'un noeud de type Meter
     */
    private ShortMeterSelectedEvent = () => {
        this.$emit('ShortMeterSelectedEvent');
    };

    /**
     * Evenement déclaré pour notifier l'evenement de recherche d'un noeud de type LBO
     */
    private ShortLBOSelectedEvent = () => {
        this.$emit('ShortLBOSelectedEvent');
    };

    //#endregion

    //#region Méthodes privées
    /** Méthode appelée lors du changement de type de recherche */
    private changePinType(typeSearch: TypeSearchEnum) {
        if(this.typeSearch === typeSearch)
            return;

        this.typeSearch = typeSearch;

        switch(typeSearch){
            case TypeSearchEnum.NameSearch:
                this.pins = [{} as Pin];
                break;
            case TypeSearchEnum.TypeSearch:
                this.pins = [FilterTreeUtils.SetPinNone(BaseObjectTypeEnum.None.toString(), this.Preview)];
                break;
            case TypeSearchEnum.PerimeterSearch:
                this.pins = [FilterTreeUtils.SetPinRoot(BaseObjectTypeEnum.None.toString())];
                break;
        }
        
        this.$emit("ChangeSearch");
    }
    
    /** Méthode appelée lors du changement de searchNode dans le composant Search */
    @Watch('searchNode')
    private SetPin(node: BaseNode) {
        this.pins = [FilterTreeUtils.SetPinTree(node, this.Preview)];
        this.searchState = this.searchNode?.Id != null && this.searchNode?.ObjectType != 'Root';
    }

    /** Retourne le type de recherche suivant le pins injecté */
    private getTypeSearch(Type: PinTypeEnum): TypeSearchEnum {
        if(Type in this.searchMap)
            return this.searchMap[Type];
    }
    //#end region

    /** Création du composant
     * initialisation du noeud de recherche
     * initialisation du type de recherche
     */
    created(){
        if(this.pins.length > 0) {
            this.typeSearch = this.getTypeSearch(this.pins[0].Type);
            this.pinType = this.pins[0].Type;
        }
    }
}

</script>
<style scoped>

</style>